import { useEffect } from "react";
import { useNavigate } from "react-router";

import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import {
  useGetDefaultTemplateName,
  useGetTemplateContentsByTemplateName,
} from "@vega-api/generated/outreach-template-contents/outreach-template-contents";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { TabNavigation } from "@vega-core/components/Layout/TabNavigation";
import { Notice } from "@vega-core/components/Notice/Notice";
import { type Tab } from "@vega-core/models/core.type";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
import { isGPCoInvestPortal } from "@vega-core/utils/global";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { GPSettingsFullPaths } from "@vega-modules/paths";
import { SettingsSectionLayout } from "@vega-modules/scenes/Settings/components/SettingsSectionLayout";

export function CoInvestmentPage() {
  const { featureFlag } = useFeatureFlag();
  const navigate = useNavigate();

  const { data: templateNameResponse, error: templateNameError } =
    useGetDefaultTemplateName();

  const { templateName = "" } = templateNameResponse?.data ?? {};

  const { data: templateData, error: contentsError } =
    useGetTemplateContentsByTemplateName(templateName, {
      query: {
        enabled: isTruthy(templateName),
      },
    });

  const templateError = templateNameError || contentsError;

  // Only check feature flag in full GP portal, not in standalone co-invest portal
  useEffect(() => {
    if (
      !isGPCoInvestPortal &&
      (!featureFlag.gp_co_investment || !buildTimeFlags.gp_co_investment_page)
    ) {
      navigate(GPSettingsFullPaths.SettingsCollaborators, { replace: true });
    }
  }, [navigate, featureFlag.gp_co_investment]);

  const tabs: Tab[] = [
    {
      index: "email-template",
      title: m.settings_coInvestment_emailTemplate(),
      component: (
        <div className="space-y-4">
          <Notice
            content={m.settings_coInvestment_emailTemplateDescription()}
          />

          <div>
            <TextareaInput
              background="bg-background/tertiary/20"
              disabled
              isOptional={false}
              label={m.settings_coInvestment_outreachEmail()}
              name="emailTemplate"
              onChange={() => {}}
              placeholder={m.coInvestments_editEmail_loadingTemplate()}
              resize={false}
              rows={15}
              value={templateData?.data?.textPart || ""}
              withBorder
            />
          </div>

          {templateError ? (
            <p className="body-sm-regular text-text/negative">
              {m.coInvestments_editEmail_templateError()}
            </p>
          ) : null}
        </div>
      ),
      isActive: true,
    },
  ];

  return (
    <SettingsSectionLayout
      description={m.settings_coInvestment_description()}
      title={m.settings_coInvestment_title()}
    >
      <TabNavigation
        innerGap={false}
        noMargin
        noSingleTab={false}
        tabClassName="rounded-companyBorderRadius border border-border/primary px-8 py-4"
        tabs={tabs}
        transparentBG
      />
    </SettingsSectionLayout>
  );
}
